import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { 
  MapPin, Clock, User, Phone, Car, Navigation, Loader2, 
  CheckCircle, AlertCircle, Calendar, ExternalLink, RefreshCw,
  MessageCircle, Star
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-yellow-500', description: 'Booking confirmed, awaiting driver assignment' },
  confirmed: { label: 'Confirmed', color: 'bg-blue-500', description: 'Booking confirmed' },
  dispatched: { label: 'Driver Assigned', color: 'bg-purple-500', description: 'Your driver is on the way' },
  in_progress: { label: 'In Progress', color: 'bg-green-500', description: 'Your trip is in progress' },
  completed: { label: 'Completed', color: 'bg-gray-500', description: 'Trip completed' },
  cancelled: { label: 'Cancelled', color: 'bg-red-500', description: 'Booking was cancelled' }
};

const DRIVER_STATUS_CONFIG = {
  en_route: { label: 'En Route', icon: Navigation, color: 'text-blue-500' },
  arrived: { label: 'Arrived', icon: MapPin, color: 'text-purple-500' },
  trip_started: { label: 'Trip Started', icon: Car, color: 'text-green-500' },
  completed: { label: 'Completed', icon: CheckCircle, color: 'text-gray-500' }
};

const TrackingPage = () => {
  const { bookingRef, token } = useParams();
  const [trackingData, setTrackingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchTrackingData = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    try {
      const response = await axios.get(`${API_URL}/api/track/${bookingRef}/${token}`);
      setTrackingData(response.data);
      setError(null);
      setLastUpdated(new Date());
    } catch (err) {
      if (err.response?.status === 404) {
        setError("Booking not found");
      } else if (err.response?.status === 403) {
        setError("Invalid tracking link");
      } else {
        setError("Failed to load tracking information");
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [bookingRef, token]);

  useEffect(() => {
    fetchTrackingData();
    
    // Poll for updates every 15 seconds when driver is active
    const interval = setInterval(() => {
      if (trackingData?.status === "dispatched" || trackingData?.status === "in_progress") {
        fetchTrackingData();
      }
    }, 15000);

    return () => clearInterval(interval);
  }, [fetchTrackingData, trackingData?.status]);

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('en-AU', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="h-10 w-10 sm:h-12 sm:w-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-white text-sm sm:text-base">Loading tracking information...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-slate-800 border-slate-700">
          <CardContent className="pt-6 text-center px-4">
            <AlertCircle className="h-10 w-10 sm:h-12 sm:w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-lg sm:text-xl font-bold text-white mb-2">Unable to Track</h2>
            <p className="text-slate-400 text-sm sm:text-base">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const statusConfig = STATUS_CONFIG[trackingData?.status] || STATUS_CONFIG.pending;
  const driverStatusConfig = DRIVER_STATUS_CONFIG[trackingData?.driver_status];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800">
      {/* Header - Mobile optimized */}
      <header className="bg-slate-900/80 backdrop-blur border-b border-slate-700 sticky top-0 z-50">
        <div className="max-w-2xl mx-auto px-3 sm:px-4">
          <div className="flex items-center justify-between h-14 sm:h-16">
            <div className="flex items-center gap-2 sm:gap-3">
              <img src="/limo-logo.jpg" alt="Limo Near Me" className="h-8 sm:h-10" />
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => fetchTrackingData(true)}
                disabled={refreshing}
                className="p-2 rounded-full hover:bg-slate-800 transition-colors"
                aria-label="Refresh tracking"
              >
                <RefreshCw className={`h-4 w-4 text-slate-400 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
              <Badge className={`${statusConfig.color} text-xs sm:text-sm px-2 sm:px-3`}>
                {statusConfig.label}
              </Badge>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-3 sm:px-4 py-4 sm:py-6 space-y-4 sm:space-y-6">
        {/* Booking Reference Card */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader className="pb-2 px-4 sm:px-6">
            <CardTitle className="text-base sm:text-lg text-slate-300">Booking Reference</CardTitle>
          </CardHeader>
          <CardContent className="px-4 sm:px-6">
            <p className="text-2xl sm:text-3xl font-mono font-bold text-primary break-all">
              {trackingData.booking_ref}
            </p>
            <p className="text-xs sm:text-sm text-slate-400 mt-1">{statusConfig.description}</p>
          </CardContent>
        </Card>

        {/* Driver Info & ETA - Mobile optimized */}
        {trackingData.driver_info && (trackingData.status === "dispatched" || trackingData.status === "in_progress") && (
          <Card className="bg-gradient-to-r from-primary/20 to-slate-800 border-primary/30 overflow-hidden">
            <CardHeader className="pb-2 px-4 sm:px-6">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base sm:text-lg text-white flex items-center gap-2">
                  <Car className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                  Your Driver
                </CardTitle>
                {/* Live indicator */}
                <div className="flex items-center gap-1.5 text-xs text-green-400">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                  </span>
                  Live
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3 sm:space-y-4 px-4 sm:px-6">
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0 ring-2 ring-primary/30">
                  <User className="h-7 w-7 sm:h-8 sm:w-8 text-primary" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xl sm:text-2xl font-bold text-white truncate">
                    {trackingData.driver_info.name}
                  </p>
                  {trackingData.driver_info.phone && (
                    <a 
                      href={`tel:${trackingData.driver_info.phone}`}
                      className="inline-flex items-center gap-2 mt-1 px-3 py-1.5 bg-primary/20 hover:bg-primary/30 rounded-full text-primary text-sm font-medium transition-colors active:scale-95"
                    >
                      <Phone className="h-4 w-4 flex-shrink-0" />
                      <span>Call Driver</span>
                    </a>
                  )}
                </div>
              </div>

              {/* Driver Status */}
              {driverStatusConfig && (
                <div className="flex items-center gap-2 py-2.5 px-4 bg-slate-900/50 rounded-xl">
                  <driverStatusConfig.icon className={`h-5 w-5 flex-shrink-0 ${driverStatusConfig.color}`} />
                  <span className="text-white font-medium text-sm sm:text-base">{driverStatusConfig.label}</span>
                </div>
              )}

              {/* ETA */}
              {trackingData.eta && (
                <div className="bg-green-500/20 border border-green-500/30 rounded-xl p-4 sm:p-5 text-center">
                  <p className="text-green-400 text-xs sm:text-sm uppercase tracking-wider font-medium">Estimated Arrival</p>
                  <p className="text-4xl sm:text-5xl font-bold text-white mt-2">{trackingData.eta.minutes}</p>
                  <p className="text-green-400 text-sm sm:text-base mt-1">minutes away</p>
                </div>
              )}

              {/* Map Link - Touch-friendly */}
              {trackingData.driver_location?.latitude && (
                <a
                  href={`https://www.google.com/maps?q=${trackingData.driver_location.latitude},${trackingData.driver_location.longitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full py-3.5 sm:py-4 bg-primary text-white rounded-xl font-semibold hover:bg-primary/90 transition-all active:scale-[0.98] text-sm sm:text-base shadow-lg shadow-primary/25"
                >
                  <Navigation className="h-5 w-5" />
                  View Driver on Map
                  <ExternalLink className="h-4 w-4 opacity-70" />
                </a>
              )}
            </CardContent>
          </Card>
        )}

        {/* Trip Details - Mobile optimized */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader className="pb-2 px-4 sm:px-6">
            <CardTitle className="text-base sm:text-lg text-slate-300">Trip Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 sm:space-y-4 px-4 sm:px-6">
            {/* Date & Time */}
            <div className="flex items-start gap-3">
              <Calendar className="h-4 w-4 sm:h-5 sm:w-5 text-primary mt-0.5 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm text-slate-400">Pickup Date & Time</p>
                <p className="text-white text-sm sm:text-base">{formatDateTime(trackingData.pickup_datetime)}</p>
              </div>
            </div>

            {/* Pickup */}
            <div className="flex items-start gap-3">
              <MapPin className="h-4 w-4 sm:h-5 sm:w-5 text-green-500 mt-0.5 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm text-slate-400">Pickup Location</p>
                <p className="text-white text-sm sm:text-base break-words">{trackingData.pickup_location}</p>
              </div>
            </div>

            {/* Dropoff */}
            {trackingData.dropoff_location && (
              <div className="flex items-start gap-3">
                <MapPin className="h-4 w-4 sm:h-5 sm:w-5 text-red-500 mt-0.5 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-xs sm:text-sm text-slate-400">Drop-off Location</p>
                  <p className="text-white text-sm sm:text-base break-words">{trackingData.dropoff_location}</p>
                </div>
              </div>
            )}

            {/* Vehicle & Passenger - Side by side on larger screens */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              {/* Vehicle */}
              <div className="flex items-start gap-3">
                <Car className="h-4 w-4 sm:h-5 sm:w-5 text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs sm:text-sm text-slate-400">Vehicle</p>
                  <p className="text-white capitalize text-sm sm:text-base">{trackingData.vehicle_type}</p>
                </div>
              </div>

              {/* Passenger */}
              <div className="flex items-start gap-3">
                <User className="h-4 w-4 sm:h-5 sm:w-5 text-primary mt-0.5 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-xs sm:text-sm text-slate-400">Passenger</p>
                  <p className="text-white text-sm sm:text-base truncate">{trackingData.passenger_name}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Completed Message with Rating */}
        {trackingData.status === "completed" && (
          <Card className="bg-green-500/20 border-green-500/30">
            <CardContent className="py-6 sm:py-8 text-center px-4">
              <CheckCircle className="h-12 w-12 sm:h-16 sm:w-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-xl sm:text-2xl font-bold text-white">Trip Completed</h3>
              <p className="text-green-300 text-sm sm:text-base mt-2">Thank you for choosing Limo Near Me!</p>
              
              {/* Rating prompt */}
              <div className="mt-6 pt-6 border-t border-green-500/30">
                <p className="text-slate-300 text-sm mb-3">How was your trip?</p>
                <div className="flex justify-center gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      className="p-2 hover:scale-110 transition-transform active:scale-95"
                      onClick={() => {/* Rating handler */}}
                    >
                      <Star className="h-8 w-8 text-yellow-500 fill-yellow-500/20 hover:fill-yellow-500" />
                    </button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Need Help Card */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="py-4 px-4 sm:px-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-primary/20">
                  <MessageCircle className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-white font-medium text-sm sm:text-base">Need Help?</p>
                  <p className="text-slate-400 text-xs sm:text-sm">Contact our support team</p>
                </div>
              </div>
              <a
                href="tel:1300759827"
                className="px-4 py-2 bg-primary/20 hover:bg-primary/30 text-primary rounded-full text-sm font-medium transition-colors active:scale-95"
              >
                Call Us
              </a>
            </div>
          </CardContent>
        </Card>

        {/* Footer - Mobile optimized */}
        <div className="text-center py-6 sm:py-8 text-slate-500 text-xs sm:text-sm space-y-2">
          <p className="font-semibold text-slate-400">Limo Near Me Australia</p>
          <p className="text-xs text-slate-500">ABN 22 680 932 003</p>
          <div className="flex items-center justify-center gap-4 pt-2">
            <a href="tel:1300759827" className="hover:text-primary transition-colors">1300 SKY TRANS</a>
            <span className="text-slate-600">•</span>
            <a href="mailto:info@limonearme.com.au" className="hover:text-primary transition-colors">Email Us</a>
          </div>
          {lastUpdated && (
            <p className="text-xs text-slate-600 mt-3">
              Last updated: {lastUpdated.toLocaleTimeString()}
            </p>
          )}
        </div>
      </main>
    </div>
  );
};

export default TrackingPage;
